// The animated launcher media a customer can choose for their chat bubble.
//
// Two sources, deliberately separate:
//
//  - BUILT-IN: whatever files sit in `widget/assets/launchers/` at build
//    time. They are content-hashed into `server/public/v1/` and listed for
//    every customer — drop a .gif in that folder, rebuild, and it appears
//    in everyone's gallery. Public by nature: they ship with the product.
//  - UPLOADED: a file one customer uploads for one bot. Stored per bot and
//    only ever listed in that bot's own dashboard — see
//    server/src/bots/launcherMedia.ts for what "private" does and does not
//    mean here (the URL has to be publicly fetchable, because the launcher
//    renders on an anonymous visitor's browser).
//
// This module holds only what both sides need to agree on: which file
// extensions are allowed, whether a file is a video or an image, and the
// size cap. The widget's build generates the built-in list; the server
// owns the uploaded ones.

/** Extensions the launcher can actually render. Videos become a <video>
 * with one <source>; everything else becomes an <img>, which is what makes
 * an animated GIF/WebP/APNG play on its own. */
export const LAUNCHER_VIDEO_EXTENSIONS = ['webm', 'mp4'] as const;
export const LAUNCHER_IMAGE_EXTENSIONS = ['gif', 'webp', 'png', 'apng', 'jpg', 'jpeg', 'svg'] as const;

export const LAUNCHER_CONTENT_TYPES: Record<string, string> = {
  webm: 'video/webm',
  mp4: 'video/mp4',
  gif: 'image/gif',
  webp: 'image/webp',
  png: 'image/png',
  apng: 'image/apng',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  svg: 'image/svg+xml',
};

/**
 * How big one uploaded launcher may be. Far below KV's own 25 MiB ceiling
 * on purpose: this file loads on every page view of the customer's site,
 * so a 10 MB GIF would be their visitors' bandwidth, their Core Web
 * Vitals, and our egress. 2 MB is already generous for a looping bubble —
 * the built-in agent video is 166 KB.
 */
export const MAX_LAUNCHER_UPLOAD_BYTES = 2 * 1024 * 1024;

export function launcherExtension(filename: string): string {
  const match = /\.([a-z0-9]+)$/i.exec(filename.trim());
  return match ? match[1].toLowerCase() : '';
}

export function isLauncherVideo(filenameOrUrl: string): boolean {
  return (LAUNCHER_VIDEO_EXTENSIONS as readonly string[]).includes(launcherExtension(filenameOrUrl.split(/[?#]/)[0]));
}

/** True for an extension the launcher can render at all. */
export function isAllowedLauncherFile(filename: string): boolean {
  const ext = launcherExtension(filename);
  return (
    (LAUNCHER_VIDEO_EXTENSIONS as readonly string[]).includes(ext) ||
    (LAUNCHER_IMAGE_EXTENSIONS as readonly string[]).includes(ext)
  );
}

export function launcherContentType(filename: string): string {
  return LAUNCHER_CONTENT_TYPES[launcherExtension(filename)] ?? 'application/octet-stream';
}

/** One entry in the built-in gallery, as the build generates it. */
export interface BuiltInLauncher {
  /** Stable across rebuilds — the source filename without its extension. */
  id: string;
  /** Shown in the dashboard: "agent-talking" becomes "Agent talking". */
  label: string;
  /** Root-relative, content-hashed: "/v1/agent-talking.ab12cd34ef.webm".
   * Root-relative rather than absolute so the same stored value works on
   * localhost and in production — the loader resolves it against the
   * origin it was served from. */
  url: string;
  video: boolean;
}

/** "agent-talking" -> "Agent talking"; "support-bot_2" -> "Support bot 2". */
export function launcherLabelFrom(basename: string): string {
  const words = basename.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : basename;
}
